import GeoFlag from '@assets/icons/geo-flag.svg';
import UsaFlag from '@assets/icons/usa-flag.svg';
import ConfirmationModal from '@components/ConfirmationModal';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Avatar, Button, Menu, MenuItem } from '@mui/material';
import i18n from 'i18next';
import React, { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// import EsFlag from '@assets/icons/es-flag.svg';
// import HyFlag from '@assets/icons/hy-flag.svg';
import RuFlag from '@assets/icons/ru-flag.svg';

type Language = {
	code: string;
	name: string;
	flag: string;
};

const languages: Language[] = [
	{ code: 'en', name: 'English', flag: UsaFlag },
	{ code: 'ka', name: 'ქართული', flag: GeoFlag },
	{ code: 'ru', name: 'Русский', flag: RuFlag },
	// { code: 'es', name: 'Español', flag: EsFlag },
	// { code: 'hy', name: 'Հայերեն', flag: HyFlag },
];

export const LanguageSwitcher: React.FC = () => {
	const { t } = useTranslation();
	const { language } = useParams();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedLanguage, setSelectedLanguage] = useState<Language>(
		languages.find((lang) => lang.code === language) || languages[0]
	);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [pendingLanguage, setPendingLanguage] = useState<Language | null>(null);

	const handleClick = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuItemClick = (language: Language) => {
		if (language.code === selectedLanguage.code) {
			setAnchorEl(null);
			return;
		}

		setPendingLanguage(language);
		setShowConfirmModal(true);
		setAnchorEl(null);
	};

	const handleConfirmLanguageChange = () => {
		if (!pendingLanguage) return;

		setSelectedLanguage(pendingLanguage);
		i18n.changeLanguage(pendingLanguage.code);

		let newPath = location.pathname.replace(
			`/${selectedLanguage.code}`,
			`/${pendingLanguage.code}`
		);
		newPath += location.search;

		// Use full reload to ensure changes take effect
		navigate(newPath, { replace: true });
		window.location.reload();
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<div className="h-[53px] flex">
			<Button onClick={handleClick} className="flex gap-[6px] !p-0">
				<Avatar
					sx={{
						width: ' 27.143px',
						height: '20px',
					}}
					variant="rounded"
					src={selectedLanguage?.flag}
					alt={`${selectedLanguage?.name} flag`}
				/>
				<KeyboardArrowDownIcon sx={{ color: '#7B809A' }} />
			</Button>
			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
				{languages.map((language) => (
					<MenuItem
						className="flex gap-[6px]"
						key={language.code}
						selected={language.code === selectedLanguage?.code}
						onClick={() => handleMenuItemClick(language)}
					>
						<Avatar
							sx={{
								width: ' 27.143px',
								height: '20px',
							}}
							variant="rounded"
							src={language.flag}
							alt={`${language.name} flag`}
							style={{ marginRight: 8 }}
						/>
						{language.name}
					</MenuItem>
				))}
			</Menu>

			<ConfirmationModal
				open={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onConfirm={handleConfirmLanguageChange}
				title={t('changeLanguage')}
				message={t('languageReloadConfirmation')}
				confirmButtonText={t('yes')}
				cancelButtonText={t('cancel')}
			/>
		</div>
	);
};
